import { useState, useEffect, useCallback } from 'react'
import { db } from '../lib/supabase'

export function useUsers() {
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    const { data, error } = await db.getUsers()
    if (error) {
      setError(error)
    } else {
      setUsers(data)
    }
    setLoading(false)
  }, [])

  const fetchInvitations = useCallback(async () => {
    const { data, error } = await db.getInvitations()
    if (!error) {
      setInvitations(data)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
    fetchInvitations()
  }, [fetchUsers, fetchInvitations])

  const updateUserRole = async (userId, role) => {
    const { data, error } = await db.updateUserRole(userId, role)
    if (!error && data) {
      setUsers(prev => prev.map(u => u.id === userId ? data : u))
    }
    return { data, error }
  }

  const updateUser = async (userId, updates) => {
    const { data, error } = await db.updateUser(userId, updates)
    if (!error && data) {
      setUsers(prev => prev.map(u => u.id === userId ? data : u))
    }
    return { data, error }
  }

  const getUserBusinessAccess = async (userId) => {
    const { data, error } = await db.getUserBusinessAccess(userId)
    if (error) {
      return { data: [], error }
    }
    return { data: data.map(access => access.business_id), error: null }
  }

  const updateUserBusinessAccess = async (userId, businessIds, currentUserId) => {
    // Get current business access for the user
    const { data: currentAccess, error: fetchError } = await db.getUserBusinessAccess(userId)
    if (fetchError) {
      return { error: fetchError }
    }

    const currentBusinessIds = currentAccess.map(access => access.business_id)

    // Determine which to add and which to remove
    const toAdd = businessIds.filter(id => !currentBusinessIds.includes(id))
    const toRemove = currentBusinessIds.filter(id => !businessIds.includes(id))

    // Grant new access
    for (const businessId of toAdd) {
      const { error } = await db.grantBusinessAccess(userId, businessId, 'member', currentUserId)
      if (error) {
        return { error }
      }
    }

    // Revoke removed access
    for (const businessId of toRemove) {
      const { error } = await db.revokeBusinessAccess(userId, businessId)
      if (error) {
        return { error }
      }
    }

    return { error: null }
  }

  const inviteUser = async (email, role, name, invitedBy) => {
    const { data, error } = await db.inviteUserByEmail(email, role, name, invitedBy)
    if (!error) {
      await fetchInvitations()
    }
    return { data, error }
  }

  const revokeInvitation = async (id) => {
    const { error } = await db.revokeInvitation(id)
    if (!error) {
      await fetchInvitations()
    }
    return { error }
  }

  const deleteUser = async (userId) => {
    const { error } = await db.deleteUser(userId)
    if (!error) {
      setUsers(prev => prev.filter(u => u.id !== userId))
    }
    return { error }
  }

  const deactivateUser = async (userId) => {
    const { data, error } = await db.deactivateUser(userId)
    if (!error && data) {
      setUsers(prev => prev.map(u => u.id === userId ? data : u))
    }
    return { data, error }
  }

  // Get counts by role
  const counts = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    members: users.filter(u => u.role === 'member').length,
    pendingInvitations: invitations.filter(i => i.status === 'pending').length,
  }

  return {
    users,
    invitations,
    counts,
    loading,
    error,
    refetch: fetchUsers,
    refetchInvitations: fetchInvitations,
    updateUserRole,
    updateUser,
    getUserBusinessAccess,
    updateUserBusinessAccess,
    inviteUser,
    revokeInvitation,
    deleteUser,
    deactivateUser,
  }
}

export default useUsers
